import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Loader2, Download, Upload, FileSpreadsheet, Search, ArrowRight } from 'lucide-react';

const ACTION_COLORS = {
  AUTO_APPLIED: 'bg-emerald-100 text-emerald-800',
  NEEDS_REVIEW: 'bg-purple-100 text-purple-800',
  NEW_UNMATCHED: 'bg-red-100 text-red-800',
  UNPRICED: 'bg-amber-100 text-amber-800',
  MISSING_ATTR: 'bg-gray-200 text-gray-800',
};
const ACTION_LABELS = {
  AUTO_APPLIED: 'Applied',
  NEEDS_REVIEW: 'Needs Review',
  NEW_UNMATCHED: 'New / Unmatched',
  UNPRICED: 'Unpriced',
  MISSING_ATTR: 'Missing Data',
};

export default function ImportTargetsModal({
  open, onClose, importFile, onFileSelect, onDownloadSample, exportAllowed,
  processing, elapsed, job, onProcess, onReset, items,
  matchRow, setMatchRow, matchSearch, setMatchSearch, onApplyMatch,
  recentJob, onResumeRecent, onDismissRecent,
}) {
  const [filter, setFilter] = useState('all');
  const results = job?.results || [];
  const stats = job?.stats;

  const filtered = results.filter((r) => {
    if (filter === 'all') return true;
    if (filter === 'applied') return r.action === 'AUTO_APPLIED';
    if (filter === 'review') return r.action === 'NEEDS_REVIEW' || r.action === 'NEW_UNMATCHED';
    if (filter === 'unpriced') return r.action === 'UNPRICED';
    if (filter === 'missing') return r.action === 'MISSING_ATTR';
    return true;
  });

  const searchResults = matchSearch.trim()
    ? items.filter((it) => it.sku?.toLowerCase().includes(matchSearch.toLowerCase()) || it.name?.toLowerCase().includes(matchSearch.toLowerCase())).slice(0, 30)
    : [];

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="import-targets-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" />Import Target Prices</DialogTitle>
          </DialogHeader>

          {!job && !processing && (
            <div className="space-y-4 py-2">
              {recentJob && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-between gap-3 text-sm" data-testid="recent-import-banner">
                  <div>
                    <p className="font-medium text-blue-900">
                      {recentJob.status === 'processing' ? 'An import is still running' : 'You have a recent import'} from {new Date(recentJob.created_at).toLocaleString()}
                    </p>
                    {recentJob.stats && (
                      <p className="text-xs text-blue-700">Total {recentJob.stats.total} · Applied {recentJob.stats.auto_applied} · Review {recentJob.stats.needs_review}</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={onDismissRecent} data-testid="dismiss-recent-import-btn">Start New</Button>
                    <Button size="sm" className="rounded-sm h-7 text-xs" onClick={onResumeRecent} data-testid="resume-recent-import-btn">
                      {recentJob.status === 'processing' ? 'Resume' : 'View Results'}
                    </Button>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label>Step 1: Download Sample File (optional)</Label>
                {exportAllowed ? (
                  <Button variant="outline" onClick={onDownloadSample} className="w-full rounded-sm" data-testid="download-sample-btn">
                    <Download className="h-4 w-4 mr-2" />Download Sample Excel
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="download-sample-disabled">Sample download isn't enabled for your account.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Step 2: Upload Your File</Label>
                <p className="text-xs text-muted-foreground">Any layout works — a "Zoho Item Name" column, or separate Brand / Frequency / Part Number / Rank columns, across multiple sheets. AI matches every row against your VSTL catalog automatically; anything uncertain is queued for your review.</p>
                <Input type="file" accept=".xlsx" onChange={onFileSelect} className="rounded-sm" data-testid="import-file-input" />
                {importFile && <p className="text-xs text-muted-foreground">Selected: {importFile.name}</p>}
              </div>
            </div>
          )}

          {processing && (
            <div className="py-10 text-center" data-testid="import-processing">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-3" />
              <p className="text-sm text-muted-foreground">AI matching your items against the VSTL catalog...</p>
              {elapsed >= 15 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {elapsed}s elapsed — large files with many new/unmatched SKUs can take several minutes.
                </p>
              )}
            </div>
          )}

          {job && stats && (
            <div className="flex-1 overflow-hidden flex flex-col space-y-3" data-testid="import-results">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 shrink-0">
                <Card className="rounded-sm"><CardContent className="p-2.5"><p className="text-[9px] text-muted-foreground uppercase">Total</p><p className="text-lg font-bold font-mono">{stats.total}</p></CardContent></Card>
                <Card className="rounded-sm bg-emerald-50"><CardContent className="p-2.5"><p className="text-[9px] text-emerald-700 uppercase">Applied</p><p className="text-lg font-bold font-mono text-emerald-700">{stats.auto_applied}</p></CardContent></Card>
                <Card className="rounded-sm bg-purple-50"><CardContent className="p-2.5"><p className="text-[9px] text-purple-700 uppercase">Review</p><p className="text-lg font-bold font-mono text-purple-700">{stats.needs_review}</p></CardContent></Card>
                <Card className="rounded-sm bg-amber-50"><CardContent className="p-2.5"><p className="text-[9px] text-amber-700 uppercase">Unpriced</p><p className="text-lg font-bold font-mono text-amber-700">{stats.unpriced}</p></CardContent></Card>
                <Card className="rounded-sm bg-red-50"><CardContent className="p-2.5"><p className="text-[9px] text-red-700 uppercase">Missing Data</p><p className="text-lg font-bold font-mono text-red-700">{stats.missing_attr}</p></CardContent></Card>
                <Card className="rounded-sm"><CardContent className="p-2.5"><p className="text-[9px] text-muted-foreground uppercase">Created/Updated</p><p className="text-lg font-bold font-mono">{stats.created}/{stats.updated}</p></CardContent></Card>
              </div>

              <div className="flex gap-1 flex-wrap shrink-0">
                {[['all', 'All'], ['applied', 'Applied'], ['review', 'Needs Review'], ['unpriced', 'Unpriced'], ['missing', 'Missing Data']].map(([k, l]) => (
                  <Button key={k} variant={filter === k ? 'default' : 'outline'} size="sm" onClick={() => setFilter(k)} className="rounded-sm text-xs h-7" data-testid={`import-filter-${k}`}>{l}</Button>
                ))}
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto border rounded-sm">
                <Table>
                  <TableHeader><TableRow className="bg-muted/50">
                    <TableHead className="text-xs">Uploaded Item</TableHead>
                    <TableHead className="text-xs">Matched SKU</TableHead>
                    <TableHead className="text-xs text-right">Price</TableHead>
                    <TableHead className="text-xs">Match</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-sm text-muted-foreground">No rows in this view</TableCell></TableRow>
                    ) : filtered.map((r, i) => (
                      <TableRow key={`${r.sheet}-${r.row_num}-${i}`} data-testid={`import-row-${i}`}>
                        <TableCell className="text-xs max-w-[220px]"><div className="whitespace-normal break-words">{r.uploaded_item}</div></TableCell>
                        <TableCell className="font-mono text-xs">{r.matched_sku || '—'}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{r.price != null ? `$${r.price}` : '—'}</TableCell>
                        <TableCell>{r.match_type ? <Badge className="text-[10px] rounded-sm">{r.match_type}</Badge> : '—'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className={`text-[10px] rounded-sm ${ACTION_COLORS[r.action] || ''}`}>{ACTION_LABELS[r.action] || r.action}</Badge>
                            {(r.action === 'NEEDS_REVIEW' || r.action === 'NEW_UNMATCHED') && (
                              <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-primary shrink-0" onClick={() => { setMatchRow(r); setMatchSearch(''); }} data-testid={`match-row-btn-${i}`}>Match</Button>
                            )}
                          </div>
                          {r.notes && <p className="text-[9px] text-muted-foreground mt-0.5">{r.notes}</p>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onReset} className="rounded-sm" data-testid="import-close-btn">Close</Button>
            {!job && <Button onClick={onProcess} disabled={processing || !importFile} className="rounded-sm" data-testid="import-process-btn">
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              {processing ? 'Processing...' : 'Process Import'}
            </Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!matchRow} onOpenChange={() => setMatchRow(null)}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-hidden flex flex-col" data-testid="manual-match-modal">
          <DialogHeader><DialogTitle>Match to VSTL Item</DialogTitle></DialogHeader>
          {matchRow && (
            <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
              <div className="p-3 bg-muted rounded-sm shrink-0">
                <p className="text-xs text-muted-foreground">Uploaded Item</p>
                <p className="text-sm font-medium">{matchRow.uploaded_item}</p>
                {matchRow.notes && <p className="text-xs text-amber-600 mt-1">{matchRow.notes}</p>}
              </div>
              {matchRow.possible_matches?.length > 0 && (
                <div className="shrink-0 max-h-[130px] overflow-y-auto">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Suggested Matches</p>
                  {matchRow.possible_matches.map((m, j) => (
                    <div key={j} className="flex items-center justify-between p-2 border rounded-sm mb-1 hover:bg-muted/30 cursor-pointer" onClick={() => onApplyMatch(matchRow, m.sku)} data-testid={`suggested-match-${j}`}>
                      <div><span className="font-mono text-xs text-primary">{m.sku}</span><span className="text-sm ml-2">{m.name}</span></div>
                      <Button variant="outline" size="sm" className="h-6 text-xs shrink-0">Select</Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="shrink-0">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Search Full Inventory</p>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input value={matchSearch} onChange={(e) => setMatchSearch(e.target.value)} placeholder="Search SKU or name..." className="pl-9 rounded-sm" autoComplete="off" data-testid="manual-match-search" />
                </div>
              </div>
              <div className="flex-1 min-h-0 border rounded-sm overflow-y-auto" style={{ maxHeight: '220px' }}>
                {searchResults.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">{matchSearch ? 'No items found' : 'Type to search'}</div>
                ) : searchResults.map((item, j) => (
                  <div key={j} className="flex items-center justify-between px-3 py-2.5 border-b last:border-b-0 hover:bg-muted/30 cursor-pointer" onClick={() => onApplyMatch(matchRow, item.sku)} data-testid={`inventory-search-result-${j}`}>
                    <div className="flex-1 min-w-0"><span className="font-mono text-xs font-medium text-primary">{item.sku}</span><p className="text-sm truncate">{item.name}</p></div>
                    <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
